import React from 'react';
import { Box } from '@mui/material';

import { CONTENT_MAX_WIDTH } from '../theme/theme';
import MobileAppBar from '../components/MobileAppBar';
import Sidebar from '../components/Sidebar';
import UpgradeDialog from '../components/UpgradeDialog';

const WIDE_MAX_WIDTH = '1200px';

function getContextUpgradeOffers(upgradeContext) {
  const nestedOffers = upgradeContext?.upgrade?.available_offers;

  if (Array.isArray(nestedOffers) && nestedOffers.length > 0) {
    return nestedOffers;
  }

  const directOffers = upgradeContext?.available_offers;

  if (Array.isArray(directOffers) && directOffers.length > 0) {
    return directOffers;
  }

  return null;
}

/**
 * Shared authenticated dashboard shell.
 * Owns chrome/layout only; panel-specific behavior belongs in PanelRenderer.
 */
export default function DashboardLayout({
  state,
  activeNav,
  activeRoute,
  mobileOpen,
  showUpgrade,
  upgradeContext,
  onMenuOpen,
  onMobileClose,
  onSwitchChild,
  onNavigate,
  onUpgrade,
  onCloseUpgrade,
  onLogout,
  children,
}) {
  const headerTitle = state.activeChild ? `${state.activeChild.first_name}'s Yearbook` : 'Yearbook Portal';
  const isWidePanel = Boolean(activeRoute?.wide);
  const contextOffers = getContextUpgradeOffers(upgradeContext);
  const upgradeOffers =
    contextOffers ||
    (Array.isArray(state.upgradeOffers)
      ? state.upgradeOffers
      : []);

  // 1. Lock the root container to exactly the window height and prevent global scrolling
  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      <MobileAppBar title={headerTitle} onMenuClick={onMenuOpen} />

      <Sidebar
        sections={state.sections}
        bookConfig={state.bookConfig}
        children={state.children}
        activeChildId={state.activeChildId}
        onSwitchChild={onSwitchChild}
        activeNav={activeNav}
        onNavigate={onNavigate}
        isPro={state.isPro}
        onUpgrade={onUpgrade}
        mobileOpen={mobileOpen}
        onMobileClose={onMobileClose}
        onLogout={onLogout}
      />

      {/* 2. Lock the main container to 100vh and allow vertical scrolling (overflowY: 'auto') */}
      <Box
        component="main"
        sx={{
          flex: 1,
          height: '100vh',
          overflowY: 'auto',
          pt: { xs: '56px', md: 0 }
        }}
      >
        <Box
          sx={{
            maxWidth: isWidePanel ? WIDE_MAX_WIDTH : CONTENT_MAX_WIDTH,
            mx: 'auto',
            px: { xs: 2.5, sm: 4, md: 5 },
            py: { xs: 3, md: 5 },
            pb: 10,
          }}
        >
          {children}
        </Box>
      </Box>

      {state.activeChild && (
        <UpgradeDialog
          open={showUpgrade}
          onClose={onCloseUpgrade}
          onNavigate={onNavigate}
          projectId={state.activeChild.project_id}
          childName={state.activeChild.first_name || state.activeChild.name}
          offers={upgradeOffers}
          upgradeContext={upgradeContext}
          onUpgradeConfirmed={
            typeof state.refreshProjectData === 'function'
              ? () => state.refreshProjectData({ showLoading: false })
              : undefined
          }
        />
      )}
    </Box>
  );
}